/**
 * CallToActionSection Component
 *
 * A reusable footer-like section that displays:
 * - Three info cards (involvement, limits explanation, no payment)
 * - CTA section with action buttons
 *
 * Used at the bottom of pages to encourage user engagement.
 */

import React from "react";
import { useNavigate } from "react-router-dom";

// =============================================================================
// Types
// =============================================================================

interface CallToActionSectionProps {
  /** Hide the info cards section */
  hideInfoCards?: boolean;
  /** Hide the CTA buttons section */
  hideCtaButtons?: boolean;
  /** Custom CTA title */
  ctaTitle?: string;
  /** Custom CTA description */
  ctaDescription?: string;
}

// =============================================================================
// CallToActionSection Component
// =============================================================================

const CallToActionSection: React.FC<CallToActionSectionProps> = ({
  hideInfoCards = false,
  hideCtaButtons = false,
  ctaTitle = "Ready to get started?",
  ctaDescription = "Scan your first book or create an account to unlock more searches.",
}) => {
  const navigate = useNavigate();

  const handleStartScanning = () => {
    navigate("/app");
  };

  const handleCreateAccount = () => {
    navigate("/login");
  };

  return (
    <section style={styles.container}>
      {/* Info Cards Section */}
      {!hideInfoCards && (
        <div style={styles.infoCardsContainer}>
          <div style={styles.infoCardsGrid}>
            {/* Card 1: Get Involved */}
            <div style={styles.infoCard}>
              <h3 style={styles.infoCardTitle}>Want To Get More Involved?</h3>
              <p style={styles.infoCardText}>
                Reach out to us at{" "}
                <a
                  href="mailto:support@secretbookprice.com"
                  style={styles.emailLink}
                >
                  support@secretbookprice.com
                </a>{" "}
                with message starting with "MORE SBP PLEASE", you can get
                involved with our extra efforts to help student entrepreneurs.
              </p>
            </div>

            {/* Card 2: Why Limits */}
            <div style={styles.infoCard}>
              <h3 style={styles.infoCardTitle}>Why limits?</h3>
              <p style={styles.infoCardText}>
                SBP compares prices from real vendors in real-time. Limits help
                us keep the service fast, reliable, and free for everyone. Power
                users can unlock unlimited searches.
              </p>
            </div>

            {/* Card 3: No Payment */}
            <div style={styles.infoCard}>
              <h3 style={styles.infoCardTitle}>No payment needed</h3>
              <p style={styles.infoCardText}>
                Every tier—Bronze, Silver, and Gold—is completely free. No
                credit card, no subscription. Just confirm your email or take a
                quick survey to unlock more features.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* CTA Section */}
      {!hideCtaButtons && (
        <div style={styles.ctaContainer}>
          <div style={styles.ctaContent}>
            <h2 style={styles.ctaTitle}>{ctaTitle}</h2>
            <p style={styles.ctaDescription}>{ctaDescription}</p>
            <div style={styles.ctaButtons}>
              <button
                style={styles.primaryButton}
                onClick={handleStartScanning}
                data-testid="cta-start-scanning"
              >
                START SCANNING
              </button>
              <button
                style={styles.secondaryButton}
                onClick={handleCreateAccount}
                data-testid="cta-create-account"
              >
                CREATE ACCOUNT
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

// =============================================================================
// Styles
// =============================================================================

const styles: Record<string, React.CSSProperties> = {
  container: {
    backgroundColor: "#f9fafb",
  },

  // Info Cards Section
  infoCardsContainer: {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "48px 24px",
  },
  infoCardsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: "24px",
  },
  infoCard: {
    backgroundColor: "#ffffff",
    borderRadius: "16px",
    padding: "32px",
    border: "1px solid #e5e7eb",
  },
  infoCardTitle: {
    fontSize: "20px",
    fontWeight: "700",
    color: "#111827",
    margin: "0 0 16px 0",
  },
  infoCardText: {
    fontSize: "15px",
    color: "#6b7280",
    lineHeight: "1.7",
    margin: 0,
  },
  emailLink: {
    color: "#6b7280",
    textDecoration: "none",
  },

  // CTA Section
  ctaContainer: {
    borderTop: "1px solid #e5e7eb",
    padding: "64px 24px",
    textAlign: "center",
  },
  ctaContent: {
    maxWidth: "600px",
    margin: "0 auto",
  },
  ctaTitle: {
    fontSize: "32px",
    fontWeight: "700",
    color: "#111827",
    margin: "0 0 16px 0",
  },
  ctaDescription: {
    fontSize: "18px",
    color: "#6b7280",
    margin: "0 0 32px 0",
    lineHeight: "1.6",
  },
  ctaButtons: {
    display: "flex",
    justifyContent: "center",
    gap: "16px",
    flexWrap: "wrap",
  },
  primaryButton: {
    padding: "16px 40px",
    backgroundColor: "#7c3aed",
    color: "#ffffff",
    border: "none",
    borderRadius: "999px",
    fontSize: "16px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "background-color 0.2s, transform 0.2s",
    letterSpacing: "0.025em",
  },
  secondaryButton: {
    padding: "16px 40px",
    backgroundColor: "#ffffff",
    color: "#7c3aed",
    border: "2px solid #7c3aed",
    borderRadius: "999px",
    fontSize: "16px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "background-color 0.2s, border-color 0.2s",
    letterSpacing: "0.025em",
  },
};

export default CallToActionSection;
